// AdminDashboard.jsx — robust data normalization + safe loading (works with your src/lib/http.js)
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  Routes,
  Route,
  Navigate,
  NavLink,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import {
  FaUsers,
  FaStoreAlt,
  FaStarHalfAlt,
  FaChartBar,
  FaClipboardList,
  FaHeartbeat,
  FaSignOutAlt,
  FaFileCsv,
  FaPlus, FaPhone,
  FaCheckCircle,
  FaBell, FaTrashAlt,
  FaUser, FaEnvelope, FaCalendarAlt, FaTimesCircle, FaMoneyBillAlt,
  FaSearch, FaExternalLinkAlt,
} from 'react-icons/fa';
import './AdminDashboard.css';
import './AuthForms.css';
import { countGendersByName } from '../lib/nameGender';
import { httpAdmin } from '../lib/http';
import { useI18n } from '../contexts/I18n';
import NotificationManagement from './NotificationManagement';
import ShopOwnerOutreach from './ShopOwnerOutreach';
import { AdminLoginPage, AdminForgotPasswordPage } from './AdminLogin';
import AdminBusinessListing from './AdminBusinessListing';
import AdminHealth from './AdminHealth';
import AdminAnalytics from './AdminAnalytics';
import AdminActivity from './AdminActivity';
import { useGuard401, ConfirmationDialog, PaginationBar, useDebouncedValue } from './adminShared';
import { TOWNS } from './shopOwnerDashboard/utils';
/* tiny helper → rows[] → blob url (download link) */
const csvify = rows =>
  URL.createObjectURL(
    new Blob([rows.map(r => Object.values(r).join(',')).join('\n')], {
      type: 'text/csv',
    }),
  );

/* ═════════════ Helpers: normalize backend shapes ═════════════ */

/**
 * Attempt to extract a usable array from backend response.
 * Accepts:
 *  - [] (array)
 *  - { data: [...] }
 *  - { items: [...] }
 *  - { results: [...] }
 *  - { docs: [...] }
 *  - { rows: [...] }
 *  - { totalsByDay: { '2025-09-01': {...}, ... } } => converts to array with _id keys
 *  - fallback: any first array found inside object
 */
function pickArray(res) {
  if (!res) return [];
  if (Array.isArray(res)) return res;

  // prefer common array keys
  const keys = [
    'data',
    'items',
    'results',
    'docs',
    'rows',
    'list',
    'users',
    'providers',
    'reviews',
    'activity',
    'bookings',
    'bookingsPerDay',
    'perDay',
    'daily',
    'series',
  ];
  for (const k of keys) {
    if (Array.isArray(res[k])) return res[k];
  }

  // totalsByDay pattern => convert object map to array
  if (res.totalsByDay && typeof res.totalsByDay === 'object') {
    try {
      return Object.entries(res.totalsByDay).map(([k, v]) => ({ ...(v || {}), _id: k }));
    } catch (e) {
      /* ignore */
    }
  }

  // find any array value
  if (typeof res === 'object') {
    for (const k in res) {
      if (Array.isArray(res[k])) return res[k];
    }
  }

  return [];
}

/** Compute a sensible total number */
function pickTotal(res, arr = []) {
  if (!res) return arr.length;
  if (typeof res.total === 'number') return res.total;
  if (typeof res.count === 'number') return res.count;
  if (typeof res.totalCount === 'number') return res.totalCount;
  if (typeof res.totals === 'number') return res.totals;
  return arr.length;
}

/* ═════════════ Sidebar button (ABSOLUTE links) ═════════════ */
const SectionButton = ({ to, icon: Icon, label, badge }) => {
  const href = to.startsWith('/admin/') ? to : `/admin/${to.replace(/^\/+/, '')}`;
  const count = Number(badge) || 0;
  return (
    <NavLink to={href} className={({ isActive }) => `adm-nav-btn${isActive ? ' active' : ''}`}>
      <Icon />
      <span>{label}</span>
      {count > 0 && (
        <span className="adm-nav-badge" aria-label={`${count} pending`}>
          {count > 99 ? '99+' : count}
        </span>
      )}
    </NavLink>
  );
};

const PROVIDER_CATEGORIES = [
  'Hair & Nail',
  'Barber',
  'Tatoo and Piercing',
  'Laser Clinics',
  'Brows & lashes',
  'Makeup',
  'Massage',
  'Pet Services',
];

const PROVIDER_CATEGORY_ALIASES = {
  'hair & nail': ['hair & nail', 'hair & nail salon'],
  'barber': ['barber', 'barbershop'],
  'tatoo and piercing': ['tatoo and piercing', 'tattoo and piercing', 'tattoo & piercing shops'],
  'tattoo and piercing': ['tatoo and piercing', 'tattoo and piercing', 'tattoo & piercing shops'],
  'laser clinics': ['laser clinics', 'hair removal'],
  'brows & lashes': ['brows & lashes', 'makeup & brows & lashes'],
  'makeup': ['makeup', 'makeup & brows & lashes'],
  'massage': ['massage', 'massage & aesthetic medicine'],
  'pet services': ['pet services'],
};

const PROVIDER_STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
];

function normProviderText(v) {
  return String(v || '').trim().toLowerCase();
}

function providerMatchesTown(provider, town) {
  if (!town) return true;
  return normProviderText(provider?.town) === normProviderText(town);
}

function providerMatchesCategory(provider, category) {
  if (!category) return true;
  const value = normProviderText(provider?.serviceCategory);
  const key = normProviderText(category);
  const aliases = PROVIDER_CATEGORY_ALIASES[key] || [key];
  return aliases.includes(value) || value === key;
}

const ADMIN_PUB = process.env.PUBLIC_URL || '';
const ADMIN_HERO_LOGO = `${ADMIN_PUB}/images/ehgezly-hero-logo.png?v=4`;

const ADMIN_PAGE_TITLES = {
  users: 'Users',
  providers: 'Providers',
  reviews: 'Reviews',
  businesses: 'List Business',
  notifications: 'Notifications',
  'shop-owner-outreach': 'Shop Owner Email',
  'delete-account-requests': 'Delete Requests',
  analytics: 'Analytics',
  activity: 'Activity',
  health: 'Health',
};

function readStoredAdminUser() {
  try {
    return JSON.parse(localStorage.getItem('adm-user') || 'null');
  } catch {
    return null;
  }
}

/* ═════════════ LOGIN ═════════════ */
/* tiny helper for profile modal display */
const cleanText = (v) => (v != null && v !== '' ? String(v) : 'N/A');

/* ═════════════ USERS (Enhanced) ═════════════ */
function Users() {
  const { t } = useI18n();
  const guard = useGuard401();
  const location = useLocation();
  const initialQ = useMemo(() => {
    try {
      return new URLSearchParams(location.search).get('q') || '';
    } catch {
      return '';
    }
  }, [location.search]);
  const [list, setList] = useState([]);
  const [total, setTot] = useState(0);
  const [q, setQ] = useState(initialQ);
  const debouncedQ = useDebouncedValue(q, 300);
  const [searchQ, setSearchQ] = useState(initialQ);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('joined');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false });
  const [profileOpen, setProfileOpen] = useState(false);
  const [loadingProf, setLoadingProf] = useState(false);
  const [clientInfo, setClientInfo] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [genderCounts, setGenderCounts] = useState({ male: 0, female: 0, unknown: 0 });

  const STATUS_FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'active', label: 'Active' },
    { id: 'inactive', label: 'Inactive' },
    { id: 'blocked', label: 'Blocked' },
    { id: 'verified', label: 'Verified' },
  ];

  useEffect(() => {
    if (!initialQ) return;
    setQ(initialQ);
    setSearchQ(initialQ);
  }, [initialQ]);

  const usersQuery = useCallback(
    (extra = {}) => {
      const params = new URLSearchParams({
        q: searchQ,
        status: statusFilter,
        sort: sortBy,
        order: sortOrder,
        page: String(extra.page ?? page),
        limit: String(extra.limit ?? pageSize),
      });
      return `/api/admin/users?${params.toString()}`;
    },
    [searchQ, statusFilter, sortBy, sortOrder, page, pageSize],
  );

  useEffect(() => {
    setSearchQ(debouncedQ);
  }, [debouncedQ]);

  useEffect(() => {
    setPage(1);
  }, [searchQ, statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    setExpandedId(null);
    setSelectedUsers([]);
  }, [searchQ, statusFilter, sortBy, sortOrder, page]);

  const runSearch = () => {
    setPage(1);
    setSearchQ(q.trim());
  };

  const loadGenderStats = useCallback(async () => {
    try {
      const res = await httpAdmin.get(usersQuery({ page: 1, limit: 5000 }));
      const items = pickArray(res);
      setGenderCounts(countGendersByName(items));
    } catch (e) {
      if (!guard(e)) console.error('Users gender stats error', e);
      setGenderCounts({ male: 0, female: 0, unknown: 0 });
    }
  }, [usersQuery, guard]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await httpAdmin.get(usersQuery());
      const items = pickArray(res);
      const tot = pickTotal(res, items);
      setList(items);
      setTot(tot);
    } catch (e) {
      if (!guard(e)) console.error('Users load error', e);
      setList([]);
      setTot(0);
    } finally {
      setLoading(false);
    }
  }, [usersQuery, guard]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadGenderStats();
  }, [loadGenderStats]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await httpAdmin.get(usersQuery({ page: 1, limit: 5000 }));
      const items = pickArray(res);
      const rows = [
        {
          name: 'Name',
          email: 'Email',
          phone: 'Phone',
          active: 'Active',
          blocked: 'Blocked',
          verified: 'Verified',
          joined: 'Joined',
        },
        ...items.map((u) => ({
          name: u.name || '',
          email: u.email || '',
          phone: u.phone || '',
          active: u.active ? 'yes' : 'no',
          blocked: u.isBlocked ? 'yes' : 'no',
          verified: u.isEmailVerified || u.isPhoneVerified ? 'yes' : 'no',
          joined: u.createdAt ? new Date(u.createdAt).toISOString() : '',
        })),
      ];
      const url = csvify(rows);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users-${statusFilter}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (!guard(e)) console.error('Users CSV export error', e);
    } finally {
      setExporting(false);
    }
  };

  const toggleUserStatus = async (id, currentStatus) => {
    try {
      await httpAdmin.put(`/api/admin/users/${id}/status`, { active: !currentStatus });
      await load();
    } catch (e) {
      if (!guard(e)) console.error('Toggle user status error:', e);
    }
  };

  const toggleUserBlock = async (id, currentBlockStatus) => {
    try {
      await httpAdmin.put(`/api/admin/users/${id}/block`, { isBlocked: !currentBlockStatus });
      await load();
    } catch (e) {
      if (!guard(e)) console.error('Toggle user block error:', e);
    }
  };

  const verifyUserEmail = async (user) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Verify email',
      message: `Mark ${user.email} as verified? Use only after confirming identity with the client.`,
      confirmText: 'Verify email',
      onConfirm: async () => {
        try {
          await httpAdmin.put(`/api/admin/users/${user._id}/verify-email`);
          await load();
          setConfirmDialog({ isOpen: false });
        } catch (e) {
          if (!guard(e)) console.error('Verify user email error:', e);
        }
      },
    });
  };

  const deleteUser = async (id) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete User",
      message: "Are you sure you want to delete this user? This action cannot be undone.",
      confirmText: "Delete",
      onConfirm: async () => {
        try {
          await httpAdmin.del(`/api/admin/users/${id}`);
          await load();
          await loadGenderStats();
          setConfirmDialog({ isOpen: false });
        } catch (e) {
          if (!guard(e)) console.error('Delete user error:', e);
        }
      }
    });
  };

  const bulkAction = async (action) => {
    if (selectedUsers.length === 0 || bulkBusy) return;

    const actionText = action === 'activate' ? 'activate' :
                      action === 'deactivate' ? 'deactivate' :
                      action === 'block' ? 'block' :
                      action === 'unblock' ? 'unblock' : 'delete';

    setConfirmDialog({
      isOpen: true,
      title: `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} Users`,
      message: `Are you sure you want to ${actionText} ${selectedUsers.length} users?`,
      confirmText: actionText.charAt(0).toUpperCase() + actionText.slice(1),
      onConfirm: async () => {
        const ids = [...selectedUsers];
        setConfirmDialog({ isOpen: false });
        setBulkBusy(true);
        setBulkProgress({ done: 0, total: ids.length });
        try {
          let done = 0;
          for (const userId of ids) {
            if (action === 'delete') {
              await httpAdmin.del(`/api/admin/users/${userId}`);
            } else if (action === 'activate' || action === 'deactivate') {
              await httpAdmin.put(`/api/admin/users/${userId}/status`, { active: action === 'activate' });
            } else if (action === 'block' || action === 'unblock') {
              await httpAdmin.put(`/api/admin/users/${userId}/block`, { isBlocked: action === 'block' });
            }
            done += 1;
            setBulkProgress({ done, total: ids.length });
          }
          setSelectedUsers([]);
          await load();
          await loadGenderStats();
        } catch (e) {
          if (!guard(e)) console.error('Bulk action error:', e);
          await load();
        } finally {
          setBulkBusy(false);
          setBulkProgress({ done: 0, total: 0 });
        }
      }
    });
  };

  const handleUserSelect = (userId, checked) => {
    if (checked) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev.filter(id => id !== userId));
    }
  };

  const selectAll = (checked) => {
    if (checked) {
      setSelectedUsers(list.map(user => user._id));
    } else {
      setSelectedUsers([]);
    }
  };

  const viewClientProfile = async (user) => {
    setLoadingProf(true);
    setProfileOpen(true);
    setClientInfo(null);
    try {
      const data = await httpAdmin.get(`/api/admin/users/${user._id}/profile`);
      setClientInfo(data);
    } catch (e) {
      if (!guard(e)) setClientInfo({ error: 'Could not load client profile.' });
    } finally {
      setLoadingProf(false);
    }
  };

  return (
    <section className="adm-page adm-page--users">
      <header className="adm-page-head">
        <h2>Users ({total})</h2>
      </header>

      <div className="adm-users-tools">
        <div className="adm-users-tools__row adm-users-tools__row--search">
          <form
            className="adm-search-group"
            onSubmit={(e) => {
              e.preventDefault();
              runSearch();
            }}
          >
            <input
              type="text"
              placeholder="Search users..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="adm-search"
              aria-label="Search users"
            />
            <button type="submit" className="adm-search-btn" aria-label="Search">
              <FaSearch aria-hidden="true" />
              <span>Search</span>
            </button>
          </form>
        </div>

        <div className="adm-users-tools__row adm-users-tools__row--filters">
          <div className="adm-filter-chips" role="group" aria-label="Filter by status">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`adm-filter-chip${statusFilter === f.id ? ' is-active' : ''}`}
                onClick={() => setStatusFilter(f.id)}
                aria-pressed={statusFilter === f.id}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="adm-users-tools__actions">
            <label className="adm-sort-control">
              <span className="adm-sort-control__label">Sort</span>
              <select
                className="adm-sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                aria-label="Sort users by"
              >
                <option value="joined">Joined</option>
                <option value="name">Name</option>
                <option value="email">Email</option>
                <option value="bookings">Bookings</option>
              </select>
              <button
                type="button"
                className="adm-sort-order-btn"
                onClick={() => setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))}
                aria-label={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </label>

            <button
              type="button"
              className="adm-export-btn"
              onClick={exportCsv}
              disabled={exporting || loading}
            >
              <FaFileCsv aria-hidden="true" />
              <span>{exporting ? 'Exporting…' : 'Export CSV'}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="adm-gender-boxes" aria-label="Users by estimated gender from first name">
        <div className="adm-gender-box adm-gender-box--male">
          <strong className="adm-gender-box-label">Male</strong>
          <strong className="adm-gender-box-value">{genderCounts.male}</strong>
        </div>
        <div className="adm-gender-box adm-gender-box--female">
          <strong className="adm-gender-box-label">Female</strong>
          <strong className="adm-gender-box-value">{genderCounts.female}</strong>
        </div>
        <div className="adm-gender-box adm-gender-box--unknown">
          <strong className="adm-gender-box-label">Unknown</strong>
          <strong className="adm-gender-box-value">{genderCounts.unknown}</strong>
        </div>
      </div>

      {selectedUsers.length > 0 && (
        <div className={`adm-bulk-actions${bulkBusy ? ' is-busy' : ''}`}>
          <span>{selectedUsers.length} selected</span>
          {bulkBusy && (
            <span className="adm-bulk-progress" aria-live="polite">
              Working… {bulkProgress.done} / {bulkProgress.total}
            </span>
          )}
          <button type="button" onClick={() => bulkAction('activate')} className="btn-success" disabled={bulkBusy}>Activate</button>
          <button type="button" onClick={() => bulkAction('deactivate')} className="btn-warning" disabled={bulkBusy}>Deactivate</button>
          <button type="button" onClick={() => bulkAction('block')} className="btn-danger" disabled={bulkBusy}>Block</button>
          <button type="button" onClick={() => bulkAction('unblock')} className="btn-info" disabled={bulkBusy}>Unblock</button>
          <button type="button" onClick={() => bulkAction('delete')} className="btn-danger" disabled={bulkBusy}>Delete</button>
          <button type="button" onClick={() => setSelectedUsers([])} className="btn-secondary" disabled={bulkBusy}>Clear</button>
        </div>
      )}

      <div className="adm-entity-list">
        {loading ? (
          <div className="adm-loading">Loading users...</div>
        ) : list.length === 0 ? (
          <div className="adm-entity-empty adm-entity-empty--users">
            <strong className="adm-entity-empty__title">No users found</strong>
            <p className="adm-entity-empty__desc">
              Try another search, clear status filters, or change sort order.
            </p>
            {(statusFilter !== 'all' || searchQ) && (
              <button
                type="button"
                className="btn-sm btn-secondary"
                onClick={() => {
                  setQ('');
                  setSearchQ('');
                  setStatusFilter('all');
                }}
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="adm-entity-list-bar">
              <label className="adm-entity-check">
                <input
                  type="checkbox"
                  checked={selectedUsers.length === list.length && list.length > 0}
                  onChange={(e) => selectAll(e.target.checked)}
                />
                <span>Select all on page</span>
              </label>
            </div>
            {list.map((user) => {
              const open = expandedId === user._id;
              const initial = String(user.name || user.email || '?').trim().charAt(0).toUpperCase();
              return (
                <article key={user._id} className={`adm-entity-card${open ? ' is-open' : ''}`}>
                  <div
                    className="adm-entity-row"
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpandedId(open ? null : user._id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setExpandedId(open ? null : user._id);
                      }
                    }}
                    aria-expanded={open}
                  >
                    <label className="adm-entity-check" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user._id)}
                        onChange={(e) => handleUserSelect(user._id, e.target.checked)}
                      />
                    </label>
                    <div className="adm-entity-avatar" aria-hidden="true">{initial}</div>
                    <div className="adm-entity-main">
                      <div className="adm-entity-name">{user.name || 'Unnamed user'}</div>
                      <div className="adm-entity-sub">{user.email || 'No email'}</div>
                    </div>
                    <div className="adm-entity-meta">
                      <span className={`badge ${user.active ? 'active' : 'inactive'}`}>
                        {user.active ? 'Active' : 'Inactive'}
                      </span>
                      {user.isBlocked && <span className="badge blocked">Blocked</span>}
                      {user.phone ? (
                        <a
                          href={`tel:${String(user.phone).replace(/\s+/g, '')}`}
                          className="adm-entity-call"
                          onClick={(e) => e.stopPropagation()}
                          title="Call"
                          aria-label="Call user"
                        >
                          <FaPhone />
                        </a>
                      ) : null}
                    </div>
                    <span className="adm-entity-chevron" aria-hidden="true">{open ? '▴' : '▾'}</span>
                  </div>

                  {open && (
                    <div className="adm-entity-panel">
                      <div className="adm-entity-stats">
                        <div className="adm-entity-stat">
                          <span className="adm-entity-stat-label">Verified</span>
                          <strong>{user.isEmailVerified || user.isPhoneVerified ? 'Yes' : 'No'}</strong>
                        </div>
                        <div className="adm-entity-stat">
                          <span className="adm-entity-stat-label">Blocked</span>
                          <strong>{user.isBlocked ? 'Yes' : 'No'}</strong>
                        </div>
                        <div className="adm-entity-stat">
                          <span className="adm-entity-stat-label">Joined</span>
                          <strong>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}</strong>
                        </div>
                        <div className="adm-entity-stat">
                          <span className="adm-entity-stat-label">Status</span>
                          <strong>{user.active ? 'Active' : 'Inactive'}</strong>
                        </div>
                      </div>
                      <div className="adm-entity-actions">
                        <button
                          type="button"
                          onClick={() => viewClientProfile(user)}
                          className="btn-sm btn-info"
                        >
                          View profile
                        </button>
                        {!(user.isEmailVerified || user.isPhoneVerified) && (
                          <button
                            type="button"
                            onClick={() => verifyUserEmail(user)}
                            className="btn-sm btn-success"
                          >
                            Verify email
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => toggleUserStatus(user._id, user.active)}
                          className={`btn-sm ${user.active ? 'btn-warning' : 'btn-success'}`}
                        >
                          {user.active ? 'Deactivate' : 'Activate'}
                        </button>
                        {user.active && (
                          <button
                            type="button"
                            onClick={() => toggleUserBlock(user._id, user.isBlocked)}
                            className={`btn-sm ${user.isBlocked ? 'btn-info' : 'btn-danger'}`}
                          >
                            {user.isBlocked ? 'Unblock' : 'Block'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => deleteUser(user._id)}
                          className="btn-sm btn-danger"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </>
        )}
      </div>
      <PaginationBar page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
      />

      {/* Client profile modal – same data/layout as shop owner dashboard */}
      {profileOpen && ReactDOM.createPortal(
        <div className="adm-client-profile-overlay" onClick={() => setProfileOpen(false)} role="dialog" aria-modal="true" aria-labelledby="adm-client-profile-title">
          <div className="adm-client-profile-modal modern-profile-modal" onClick={e => e.stopPropagation()}>
            <div className="modern-profile-modal-header">
              <div className="modern-profile-modal-header-content">
                <div className="modern-profile-modal-title-container">
                  <FaUser className="modern-profile-modal-icon" />
                  <h3 id="adm-client-profile-title" className="modern-profile-modal-title">{t('ownerClientProfile') || 'Client Profile'}</h3>
                </div>
              </div>
              <button type="button" className="modern-profile-modal-close" onClick={() => setProfileOpen(false)} aria-label="Close">×</button>
            </div>
            {loadingProf ? (
              <div className="client-modal-loading"><div>{t('ownerLoadingClientProfile') || 'Loading client profile...'}</div></div>
            ) : clientInfo?.error ? (
              <div className="client-modal-error"><div>{clientInfo.error}</div></div>
            ) : clientInfo?.profile ? (
              <div className="modern-profile-modal-body">
                <div className="modern-profile-section">
                  <div className="modern-profile-section-header">
                    <div className="modern-profile-section-header-left">
                      <div className="modern-profile-section-icon"><FaUser /></div>
                      <h4 className="modern-profile-section-title">Personal Information</h4>
                    </div>
                  </div>
                  <div className="modern-profile-info-card">
                    <div className="modern-profile-info-item">
                      <div className="modern-profile-info-icon"><FaUser /></div>
                      <div className="modern-profile-info-content">
                        <div className="modern-profile-info-label">Name</div>
                        <div className="modern-profile-info-value">{clientInfo.profile.name || 'N/A'}</div>
                      </div>
                    </div>
                    <div className="modern-profile-divider" />
                    {clientInfo.profile.email != null && clientInfo.profile.email !== '' && (
                      <div className="modern-profile-phone-item">
                        <div className="modern-profile-contact-row">
                          <div className="modern-profile-info-icon"><FaEnvelope /></div>
                          <div className="modern-profile-info-content">
                            <div className="modern-profile-info-label">Email</div>
                            <div className="modern-profile-info-value">{clientInfo.profile.email || 'N/A'}</div>
                          </div>
                        </div>
                        <a href={`mailto:${clientInfo.profile.email}`} className="modern-contact-action-button">Email Now</a>
                      </div>
                    )}
                    {clientInfo.profile.phone != null && clientInfo.profile.phone !== '' && (
                      <>
                        <div className="modern-profile-divider" />
                        <div className="modern-profile-phone-item">
                          <div className="modern-profile-contact-row">
                            <div className="modern-profile-info-icon"><FaPhone /></div>
                            <div className="modern-profile-info-content">
                              <div className="modern-profile-info-label">Phone</div>
                              <div className="modern-profile-info-value">{clientInfo.profile.phone || 'N/A'}</div>
                            </div>
                          </div>
                          <a href={`tel:${clientInfo.profile.phone}`} className="modern-contact-action-button">Call Now</a>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {clientInfo.stats && (
                  <div className="modern-profile-section">
                    <div className="modern-profile-section-header">
                      <div className="modern-profile-section-header-left">
                        <div className="modern-profile-section-icon"><FaChartBar /></div>
                        <h4 className="modern-profile-section-title">Booking Statistics</h4>
                      </div>
                    </div>
                    <div className="modern-stats-grid">
                      <div className="modern-stat-card">
                        <div className="modern-stat-icon"><FaCalendarAlt /></div>
                        <div className="modern-stat-value">{cleanText(clientInfo.stats?.totalBookings ?? clientInfo.stats?.total ?? 0)}</div>
                        <div className="modern-stat-label">Total Bookings</div>
                      </div>
                      <div className="modern-stat-card">
                        <div className="modern-stat-icon"><FaCheckCircle /></div>
                        <div className="modern-stat-value">{cleanText(clientInfo.stats.completed ?? 0)}</div>
                        <div className="modern-stat-label">Completed</div>
                      </div>
                      <div className="modern-stat-card">
                        <div className="modern-stat-icon"><FaTimesCircle /></div>
                        <div className="modern-stat-value">{cleanText(clientInfo.stats.cancelled ?? 0)}</div>
                        <div className="modern-stat-label">Cancelled</div>
                      </div>
                      {clientInfo.stats?.spendEGP != null && (
                        <div className="modern-stat-card">
                          <div className="modern-stat-icon"><FaMoneyBillAlt /></div>
                          <div className="modern-stat-value">{cleanText(`${clientInfo.stats.spendEGP} EGP`)}</div>
                          <div className="modern-stat-label">Total Spent (EGP)</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}


/* ═════════════ PROVIDERS (Enhanced) ═════════════ */
function Providers({ onPendingCountChange }) {
  const guard = useGuard401();
  const nav = useNavigate();
  const location = useLocation();
  const initialQ = useMemo(() => {
    try {
      return new URLSearchParams(location.search).get('q') || '';
    } catch {
      return '';
    }
  }, [location.search]);
  const [rows, setRows] = useState([]);
  const [total, setTot] = useState(0);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [state, setState] = useState('all');
  const [q, setQ] = useState(initialQ);
  const [searchQ, setSearchQ] = useState(initialQ);
  const debouncedQ = useDebouncedValue(q, 350);
  const [townFilter, setTownFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortBy, setSortBy] = useState('created');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [busy, setBusy] = useState(false);
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false });
  const [profileOpen, setProfileOpen] = useState(false);
  const [loadingProf, setLoadingProf] = useState(false);
  const [providerInfo, setProviderInfo] = useState(null);
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [profileFilterMonth, setProfileFilterMonth] = useState(null); // null = all time, 1-12 = month
  const [profileFilterYear, setProfileFilterYear] = useState(null);   // null = all time, e.g. 2025
  const [expandedId, setExpandedId] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [facetTowns, setFacetTowns] = useState([]);
  const [facetCategories, setFacetCategories] = useState([]);

  useEffect(() => {
    if (!initialQ) return;
    setQ(initialQ);
    setSearchQ(initialQ);
  }, [initialQ]);

  const providersQuery = useCallback(
    (extra = {}) => {
      const params = new URLSearchParams();
      params.set('state', extra.state ?? state);
      params.set('page', String(extra.page ?? page));
      params.set('limit', String(extra.limit ?? pageSize));
      params.set('sort', sortBy);
      params.set('order', sortOrder);
      if (searchQ) params.set('q', searchQ);
      if (townFilter) params.set('town', townFilter);
      if (categoryFilter) params.set('category', categoryFilter);
      return `/api/admin/providers?${params.toString()}`;
    },
    [state, searchQ, townFilter, categoryFilter, sortBy, sortOrder, page, pageSize],
  );

  const townOptions = useMemo(() => {
    const fromFacets = Array.isArray(facetTowns) ? facetTowns : [];
    const merged = [...new Set([...TOWNS, ...fromFacets].filter(Boolean))];
    return merged.sort((a, b) => String(a).localeCompare(String(b)));
  }, [facetTowns]);

  const categoryOptions = useMemo(() => {
    const fromFacets = Array.isArray(facetCategories) ? facetCategories : [];
    const merged = [...new Set([...PROVIDER_CATEGORIES, ...fromFacets].filter(Boolean))];
    return merged.sort((a, b) => String(a).localeCompare(String(b)));
  }, [facetCategories]);

  useEffect(() => {
    setSearchQ(debouncedQ.trim());
  }, [debouncedQ]);

  useEffect(() => {
    setPage(1);
  }, [state, searchQ, townFilter, categoryFilter, sortBy, sortOrder]);

  useEffect(() => {
    setExpandedId(null);
    setSelectedProviders([]);
  }, [state, searchQ, townFilter, categoryFilter, sortBy, sortOrder, page]);

  const load = useCallback(async () => {
    setBusy(true);
    setLoadError('');
    try {
      const hasLocalFilters = Boolean(townFilter || categoryFilter);
      // When town/category are set, pull a wide page then filter locally so
      // results stay correct even if production API ignores those params yet.
      const res = await httpAdmin.get(
        providersQuery({
          page: hasLocalFilters ? 1 : page,
          limit: hasLocalFilters ? 5000 : pageSize,
        }),
      );
      let items = pickArray(res);

      if (Array.isArray(res?.facets?.towns)) setFacetTowns(res.facets.towns);
      if (Array.isArray(res?.facets?.categories)) setFacetCategories(res.facets.categories);

      if (townFilter) {
        items = items.filter((p) => providerMatchesTown(p, townFilter));
      }
      if (categoryFilter) {
        items = items.filter((p) => providerMatchesCategory(p, categoryFilter));
      }

      // Client-side sort when we filtered locally (full set in memory)
      if (hasLocalFilters) {
        const dir = sortOrder === 'asc' ? 1 : -1;
        items = [...items].sort((a, b) => {
          const av =
            sortBy === 'name' ? a?.name :
            sortBy === 'town' ? a?.town :
            sortBy === 'category' ? a?.serviceCategory :
            a?.createdAt;
          const bv =
            sortBy === 'name' ? b?.name :
            sortBy === 'town' ? b?.town :
            sortBy === 'category' ? b?.serviceCategory :
            b?.createdAt;
          return String(av || '').localeCompare(String(bv || ''), undefined, { sensitivity: 'base' }) * dir;
        });
        const totalFiltered = items.length;
        const start = (page - 1) * pageSize;
        setTot(totalFiltered);
        setRows(items.slice(start, start + pageSize));
      } else {
        setRows(items);
        setTot(pickTotal(res, items));
      }

      let nextStats = {
        pending: Number(res?.stats?.pending),
        approved: Number(res?.stats?.approved),
        rejected: Number(res?.stats?.rejected),
      };

      // Fallback when API has not been redeployed yet (no stats field)
      const statsMissing =
        !Number.isFinite(nextStats.pending) &&
        !Number.isFinite(nextStats.approved) &&
        !Number.isFinite(nextStats.rejected);

      if (statsMissing) {
        const [pendingRes, approvedRes, rejectedRes] = await Promise.all([
          httpAdmin.get('/api/admin/providers?state=pending&page=1&limit=1'),
          httpAdmin.get('/api/admin/providers?state=approved&page=1&limit=1'),
          httpAdmin.get('/api/admin/providers?state=rejected&page=1&limit=1'),
        ]);
        nextStats = {
          pending: pickTotal(pendingRes, pickArray(pendingRes)),
          approved: pickTotal(approvedRes, pickArray(approvedRes)),
          rejected: pickTotal(rejectedRes, pickArray(rejectedRes)),
        };
      } else {
        nextStats = {
          pending: nextStats.pending || 0,
          approved: nextStats.approved || 0,
          rejected: nextStats.rejected || 0,
        };
      }

      setStats(nextStats);
      onPendingCountChange?.(nextStats.pending);
    } catch (e) {
      if (!guard(e)) {
        console.error('Providers load error', e);
        setLoadError(e?.message || 'Could not load providers');
      }
      setRows([]);
      setTot(0);
      setStats({ pending: 0, approved: 0, rejected: 0 });
    } finally {
      setBusy(false);
    }
  }, [guard, providersQuery, onPendingCountChange, townFilter, categoryFilter, page, pageSize, sortBy, sortOrder]);

  useEffect(() => {
    load();
  }, [load]);

  const runSearch = () => {
    setPage(1);
    setSearchQ(q.trim());
  };

  const approveProvider = async (id) => {
    try {
      await httpAdmin.put(`/api/admin/providers/${id}/approve`);
      await load();
    } catch (e) {
      if (!guard(e)) console.error('Approve provider error:', e);
    }
  };

  const rejectProvider = async (id) => {
    setConfirmDialog({
      isOpen: true,
      title: "Reject application",
      message: "Reject this pending application? The listing will be kept as Rejected (not deleted) and will not appear publicly.",
      confirmText: "Reject",
      onConfirm: async () => {
        try {
          await httpAdmin.del(`/api/admin/providers/${id}`);
          await load();
          setConfirmDialog({ isOpen: false });
        } catch (e) {
          if (!guard(e)) console.error('Reject provider error:', e);
        }
      }
    });
  };

  const deleteProvider = async (id, providerName) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete listing permanently",
      message: `Permanently delete "${providerName}"? This cannot be undone and removes associated listing data.`,
      confirmText: "Delete Permanently",
      onConfirm: async () => {
        try {
          await httpAdmin.del(`/api/admin/providers/${id}/delete`);
          await load();
          setConfirmDialog({ isOpen: false });
        } catch (e) {
          if (!guard(e)) console.error('Delete provider error:', e);
        }
      }
    });
  };

  const toggleProviderBlock = async (id, currentBlockStatus) => {
    try {
      await httpAdmin.put(`/api/admin/providers/${id}/block`, { isBlocked: !currentBlockStatus });
      await load();
    } catch (e) {
      if (!guard(e)) console.error('Toggle provider block error:', e);
    }
  };

  const bulkAction = async (action) => {
    if (selectedProviders.length === 0 || bulkBusy) return;

    const labels = {
      approve: { title: 'Approve providers', message: `Approve ${selectedProviders.length} selected provider(s)?`, confirm: 'Approve' },
      reject: { title: 'Reject providers', message: `Reject ${selectedProviders.length} selected provider(s)? They will stay as Rejected (not deleted).`, confirm: 'Reject' },
      block: { title: 'Block providers', message: `Block ${selectedProviders.length} selected provider(s)?`, confirm: 'Block' },
      unblock: { title: 'Unblock providers', message: `Unblock ${selectedProviders.length} selected provider(s)?`, confirm: 'Unblock' },
      delete: { title: 'Delete providers', message: `Permanently delete ${selectedProviders.length} selected provider(s)? This cannot be undone.`, confirm: 'Delete' },
    };
    const meta = labels[action];
    if (!meta) return;

    setConfirmDialog({
      isOpen: true,
      title: meta.title,
      message: meta.message,
      confirmText: meta.confirm,
      onConfirm: async () => {
        const ids = [...selectedProviders];
        setConfirmDialog({ isOpen: false });
        setBulkBusy(true);
        setBulkProgress({ done: 0, total: ids.length });
        try {
          let done = 0;
          for (const providerId of ids) {
            if (action === 'approve') {
              await httpAdmin.put(`/api/admin/providers/${providerId}/approve`);
            } else if (action === 'reject') {
              await httpAdmin.del(`/api/admin/providers/${providerId}`);
            } else if (action === 'block' || action === 'unblock') {
              await httpAdmin.put(`/api/admin/providers/${providerId}/block`, {
                isBlocked: action === 'block',
              });
            } else if (action === 'delete') {
              await httpAdmin.del(`/api/admin/providers/${providerId}/delete`);
            }
            done += 1;
            setBulkProgress({ done, total: ids.length });
          }
          setSelectedProviders([]);
          await load();
        } catch (e) {
          if (!guard(e)) console.error('Bulk provider action error:', e);
          await load();
        } finally {
          setBulkBusy(false);
          setBulkProgress({ done: 0, total: 0 });
        }
      },
    });
  };

  const fetchProviderProfile = useCallback(async (providerId, month, year) => {
    const params = new URLSearchParams();
    if (month != null && year != null) {
      params.set('month', String(month));
      params.set('year', String(year));
    }
    const qs = params.toString();
    return httpAdmin.get(`/api/admin/providers/${providerId}/profile${qs ? `?${qs}` : ''}`);
  }, []);

  const viewProviderProfile = (provider) => {
    setSelectedProviderId(provider._id);
    setProfileFilterMonth(null);
    setProfileFilterYear(null);
    setProviderInfo(null);
    setProfileOpen(true);
  };

  const publicListingUrl = (provider) => {
    const key = provider.slug || provider.name;
    if (!key) return null;
    return `/booking/${encodeURIComponent(key)}`;
  };

  // Refetch provider stats when modal is open and filter (month/year) is complete: all-time (null,null) or specific month (m,y)
  useEffect(() => {
    if (!profileOpen || !selectedProviderId) return;
    const month = profileFilterMonth;
    const year = profileFilterYear;
    const complete = (month == null && year == null) || (month != null && year != null);
    if (!complete) return;
    setLoadingProf(true);
    fetchProviderProfile(selectedProviderId, month, year)
      .then((data) => setProviderInfo(data))
      .catch((e) => {
        if (!guard(e)) setProviderInfo((prev) => ({ ...(prev || {}), error: 'Could not load provider profile.' }));
      })
      .finally(() => setLoadingProf(false));
  }, [profileOpen, selectedProviderId, profileFilterMonth, profileFilterYear, fetchProviderProfile, guard]);

  const statusLabel = (provider) => {
    if (provider.approved) return 'Approved';
    if (provider.rejected) return 'Rejected';
    return 'Pending';
  };

  const statusBadgeClass = (provider) => {
    if (provider.approved) return 'approved';
    if (provider.rejected) return 'blocked';
    return 'pending';
  };

  const isPending = (provider) => !provider.approved && !provider.rejected;
  const hasActiveFilters = state !== 'all' || searchQ || townFilter || categoryFilter;

  return (
    <section className="adm-page adm-page--providers">
      <header className="adm-page-head">
        <h2>Providers ({total})</h2>
      </header>

      <div className="adm-users-tools adm-providers-tools">
        <div className="adm-users-tools__row adm-users-tools__row--search">
          <form
            className="adm-search-group"
            onSubmit={(e) => {
              e.preventDefault();
              runSearch();
            }}
          >
            <input
              type="text"
              placeholder="Search shop, town, phone, email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="adm-search"
              aria-label="Search providers"
            />
            <button type="submit" className="adm-search-btn" aria-label="Search">
              <FaSearch aria-hidden="true" />
              <span>Search</span>
            </button>
          </form>
        </div>

        <div className="adm-users-tools__row adm-users-tools__row--filters">
          <div className="adm-filter-chips" role="group" aria-label="Filter by status">
            {PROVIDER_STATUS_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`adm-filter-chip${state === f.id ? ' is-active' : ''}`}
                onClick={() => setState(f.id)}
                aria-pressed={state === f.id}
              >
                {f.label}
                {f.id === 'pending' && stats.pending > 0 ? ` (${stats.pending})` : ''}
              </button>
            ))}
          </div>

          <div className="adm-users-tools__actions adm-providers-tools__actions">
            <label className="adm-sort-control">
              <span className="adm-sort-control__label">Town</span>
              <select
                className="adm-sort-select"
                value={townFilter}
                onChange={(e) => setTownFilter(e.target.value)}
                aria-label="Filter by town"
              >
                <option value="">All towns</option>
                {townOptions.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>

            <label className="adm-sort-control">
              <span className="adm-sort-control__label">Category</span>
              <select
                className="adm-sort-select"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                aria-label="Filter by category"
              >
                <option value="">All categories</option>
                {categoryOptions.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>

            <div className="adm-providers-sort-row">
              <label className="adm-sort-control">
                <span className="adm-sort-control__label">Sort</span>
                <select
                  className="adm-sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  aria-label="Sort providers by"
                >
                  <option value="created">Joined</option>
                  <option value="name">Name</option>
                  <option value="town">Town</option>
                  <option value="category">Category</option>
                </select>
              </label>
              <button
                type="button"
                className="adm-sort-order-btn"
                onClick={() => setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))}
                aria-label={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="adm-gender-boxes adm-provider-stats" aria-label="Provider moderation counts">
        <button
          type="button"
          className={`adm-gender-box adm-gender-box--unknown adm-provider-stat${state === 'pending' ? ' is-active' : ''}`}
          onClick={() => setState('pending')}
        >
          <strong className="adm-gender-box-label">Pending</strong>
          <strong className="adm-gender-box-value">{stats.pending}</strong>
        </button>
        <button
          type="button"
          className={`adm-gender-box adm-gender-box--male adm-provider-stat${state === 'approved' ? ' is-active' : ''}`}
          onClick={() => setState('approved')}
        >
          <strong className="adm-gender-box-label">Approved</strong>
          <strong className="adm-gender-box-value">{stats.approved}</strong>
        </button>
        <button
          type="button"
          className={`adm-gender-box adm-gender-box--female adm-provider-stat${state === 'rejected' ? ' is-active' : ''}`}
          onClick={() => setState('rejected')}
        >
          <strong className="adm-gender-box-label">Rejected</strong>
          <strong className="adm-gender-box-value">{stats.rejected}</strong>
        </button>
      </div>

      {selectedProviders.length > 0 && (
        <div className={`adm-bulk-actions${bulkBusy ? ' is-busy' : ''}`}>
          <span>{selectedProviders.length} selected</span>
          {bulkBusy && (
            <span className="adm-bulk-progress" aria-live="polite">
              Working… {bulkProgress.done} / {bulkProgress.total}
            </span>
          )}
          <button type="button" onClick={() => bulkAction('approve')} className="btn-success" disabled={bulkBusy}>Approve</button>
          <button type="button" onClick={() => bulkAction('reject')} className="btn-warning" disabled={bulkBusy}>Reject</button>
          <button type="button" onClick={() => bulkAction('block')} className="btn-danger" disabled={bulkBusy}>Block</button>
          <button type="button" onClick={() => bulkAction('unblock')} className="btn-info" disabled={bulkBusy}>Unblock</button>
          <button type="button" onClick={() => bulkAction('delete')} className="btn-danger" disabled={bulkBusy}>Delete</button>
          <button type="button" onClick={() => setSelectedProviders([])} className="btn-secondary" disabled={bulkBusy}>Clear</button>
        </div>
      )}

      {loadError && (
        <div className="error-message" role="alert" style={{ marginBottom: '1rem' }}>
          {loadError}
          <button type="button" className="btn-sm btn-secondary" style={{ marginInlineStart: '0.75rem' }} onClick={load}>
            Retry
          </button>
        </div>
      )}

      <div className="adm-entity-list">
        {busy ? (
          <div className="adm-loading">Loading providers...</div>
        ) : rows.length === 0 ? (
          <div className="adm-entity-empty adm-entity-empty--users">
            <strong className="adm-entity-empty__title">
              {loadError ? 'Could not load providers' : 'No providers found'}
            </strong>
            <p className="adm-entity-empty__desc">
              {loadError
                ? 'The providers API failed. Redeploy the backend if you just added Providers search/stats, then retry.'
                : 'Try another search, clear filters, or change sort order.'}
            </p>
            {hasActiveFilters && !loadError && (
              <button
                type="button"
                className="btn-sm btn-secondary"
                onClick={() => {
                  setQ('');
                  setSearchQ('');
                  setState('all');
                  setTownFilter('');
                  setCategoryFilter('');
                }}
              >
                Clear filters
              </button>
            )}
            {loadError && (
              <button type="button" className="btn-sm btn-secondary" onClick={load}>
                Retry
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="adm-entity-list-bar">
              <label className="adm-entity-check">
                <input
                  type="checkbox"
                  checked={selectedProviders.length === rows.length && rows.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedProviders(rows.map((r) => r._id));
                    } else {
                      setSelectedProviders([]);
                    }
                  }}
                />
                <span>Select all on page</span>
              </label>
            </div>
            {rows.map((provider) => {
              const open = expandedId === provider._id;
              const pending = isPending(provider);
              const initial = String(provider.name || '?').trim().charAt(0).toUpperCase();
              const listingUrl = publicListingUrl(provider);
              return (
                <article
                  key={provider._id}
                  className={`adm-entity-card${open ? ' is-open' : ''}${pending ? ' is-pending' : ''}`}
                >
                  <div
                    className="adm-entity-row"
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpandedId(open ? null : provider._id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setExpandedId(open ? null : provider._id);
                      }
                    }}
                    aria-expanded={open}
                  >
                    <label className="adm-entity-check" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedProviders.includes(provider._id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedProviders((prev) => [...prev, provider._id]);
                          } else {
                            setSelectedProviders((prev) => prev.filter((id) => id !== provider._id));
                          }
                        }}
                      />
                    </label>
                    {provider.imageUrl ? (
                      <img
                        src={provider.imageUrl}
                        alt=""
                        className="adm-entity-avatar adm-entity-avatar--img"
                        aria-hidden="true"
                      />
                    ) : (
                      <div className="adm-entity-avatar" aria-hidden="true">{initial}</div>
                    )}
                    <div className="adm-entity-main">
                      <div className="adm-entity-name">
                        {provider.name || 'Unnamed provider'}
                        {pending && <span className="adm-pending-pill">Needs review</span>}
                      </div>
                      <div className="adm-entity-sub">
                        {[provider.serviceCategory, provider.town, provider.phone].filter(Boolean).join(' · ') || 'No details'}
                      </div>
                    </div>
                    <div className="adm-entity-meta">
                      <span className={`badge ${statusBadgeClass(provider)}`}>
                        {statusLabel(provider)}
                      </span>
                      {provider.isBlocked && <span className="badge blocked">Blocked</span>}
                    </div>
                    <span className="adm-entity-chevron" aria-hidden="true">{open ? '▴' : '▾'}</span>
                  </div>

                  {open && (
                    <div className="adm-entity-panel">
                      {provider.imageUrl && (
                        <div className="adm-provider-thumb-wrap">
                          <img
                            src={provider.imageUrl}
                            alt={provider.name || 'Provider'}
                            className="adm-provider-thumb"
                          />
                        </div>
                      )}
                      <div className="adm-entity-stats">
                        <div className="adm-entity-stat">
                          <span className="adm-entity-stat-label">Category</span>
                          <strong>{provider.serviceCategory || '—'}</strong>
                        </div>
                        <div className="adm-entity-stat">
                          <span className="adm-entity-stat-label">Town</span>
                          <strong>{provider.town || '—'}</strong>
                        </div>
                        <div className="adm-entity-stat">
                          <span className="adm-entity-stat-label">Phone</span>
                          <strong>{provider.phone || '—'}</strong>
                        </div>
                        <div className="adm-entity-stat">
                          <span className="adm-entity-stat-label">Status</span>
                          <strong>{statusLabel(provider)}</strong>
                        </div>
                      </div>
                      <div className="adm-entity-actions">
                        <button
                          type="button"
                          onClick={() => viewProviderProfile(provider)}
                          className="btn-sm btn-info"
                          title="View provider profile"
                        >
                          View profile
                        </button>
                        <button
                          type="button"
                          onClick={() => nav(`/admin/businesses?edit=${provider._id}`)}
                          className="btn-sm btn-secondary"
                          title="Edit business listing"
                        >
                          Edit listing
                        </button>
                        {listingUrl && (
                          <a
                            href={listingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-sm btn-secondary adm-provider-listing-link"
                            title="Open public listing"
                          >
                            <FaExternalLinkAlt aria-hidden="true" />
                            <span>View listing</span>
                          </a>
                        )}
                        {!provider.approved && (
                          <button
                            type="button"
                            onClick={() => approveProvider(provider._id)}
                            className="btn-sm btn-success"
                            title={provider.rejected ? 'Approve previously rejected application' : 'Approve application'}
                          >
                            Approve
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => toggleProviderBlock(provider._id, provider.isBlocked)}
                          className={`btn-sm ${provider.isBlocked ? 'btn-info' : 'btn-danger'}`}
                        >
                          {provider.isBlocked ? 'Unblock' : 'Block'}
                        </button>
                        {!provider.approved && !provider.rejected && (
                          <button
                            type="button"
                            onClick={() => rejectProvider(provider._id)}
                            className="btn-sm btn-danger"
                            title="Soft-reject application (keeps record)"
                          >
                            Reject
                          </button>
                        )}
                        {(provider.approved || provider.rejected) && (
                          <button
                            type="button"
                            onClick={() => deleteProvider(provider._id, provider.name)}
                            className="btn-sm btn-danger"
                            title="Permanently remove this listing"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </>
        )}
      </div>
      <PaginationBar page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
      />

      {/* Provider profile modal: shop name, phone, total appointments, revenue */}
      {profileOpen && ReactDOM.createPortal(
        <div className="adm-client-profile-overlay" onClick={() => setProfileOpen(false)} role="dialog" aria-modal="true" aria-labelledby="adm-provider-profile-title">
          <div className="adm-client-profile-modal adm-provider-profile-modal modern-profile-modal" onClick={e => e.stopPropagation()}>
            <div className="modern-profile-modal-header">
              <div className="modern-profile-modal-header-content">
                <div className="modern-profile-modal-title-container">
                  <FaStoreAlt className="modern-profile-modal-icon" />
                  <h3 id="adm-provider-profile-title" className="modern-profile-modal-title">Provider / Shop Profile</h3>
                </div>
              </div>
              <button type="button" className="modern-profile-modal-close" onClick={() => setProfileOpen(false)} aria-label="Close">×</button>
            </div>
            {loadingProf ? (
              <div className="client-modal-loading"><div>Loading provider profile...</div></div>
            ) : providerInfo?.error ? (
              <div className="client-modal-error"><div>{providerInfo.error}</div></div>
            ) : providerInfo ? (
              <div className="modern-profile-modal-body">
                <div className="modern-profile-section">
                  <div className="modern-profile-section-header">
                    <div className="modern-profile-section-header-left">
                      <div className="modern-profile-section-icon"><FaStoreAlt /></div>
                      <h4 className="modern-profile-section-title">Shop Information</h4>
                    </div>
                  </div>
                  <div className="modern-profile-info-card">
                    <div className="modern-profile-info-item">
                      <div className="modern-profile-info-icon"><FaStoreAlt /></div>
                      <div className="modern-profile-info-content">
                        <div className="modern-profile-info-label">Shop name</div>
                        <div className="modern-profile-info-value">{providerInfo.shopName || 'N/A'}</div>
                      </div>
                    </div>
                    <div className="modern-profile-divider" />
                    {providerInfo.phone != null && providerInfo.phone !== '' && (
                      <div className="modern-profile-phone-item">
                        <div className="modern-profile-contact-row">
                          <div className="modern-profile-info-icon"><FaPhone /></div>
                          <div className="modern-profile-info-content">
                            <div className="modern-profile-info-label">Phone / Number</div>
                            <div className="modern-profile-info-value">{providerInfo.phone}</div>
                          </div>
                        </div>
                        <a href={`tel:${providerInfo.phone}`} className="modern-contact-action-button">Call</a>
                      </div>
                    )}
                  </div>
                </div>
                <div className="modern-profile-section">
                  <div className="modern-profile-section-header">
                    <div className="modern-profile-section-header-left">
                      <div className="modern-profile-section-icon"><FaChartBar /></div>
                      <h4 className="modern-profile-section-title">Statistics</h4>
                    </div>
                  </div>
                  <div className="adm-provider-profile-filter">
                    <label className="adm-provider-filter-label">Filter by month</label>
                    <div className="adm-provider-filter-row">
                      <select
                        className="adm-provider-filter-select"
                        value={profileFilterMonth ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          const month = v === '' ? null : parseInt(v, 10);
                          setProfileFilterMonth(month);
                          if (month == null) setProfileFilterYear(null);
                        }}
                        aria-label="Select month"
                      >
                        <option value="">All time</option>
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                          <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('en-US', { month: 'long' })}</option>
                        ))}
                      </select>
                      <select
                        className="adm-provider-filter-select"
                        value={profileFilterYear ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          const year = v === '' ? null : parseInt(v, 10);
                          setProfileFilterYear(year);
                        }}
                        aria-label="Select year"
                      >
                        <option value="">{profileFilterMonth != null ? 'Year…' : 'All time'}</option>
                        {(() => {
                          const currentYear = new Date().getFullYear();
                          return Array.from({ length: 6 }, (_, i) => currentYear - i);
                        })().map(y => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                    {(providerInfo.filterMonth != null && providerInfo.filterYear != null) && (
                      <div className="adm-provider-filter-period">
                        Showing: {new Date(providerInfo.filterYear, providerInfo.filterMonth - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' })}
                      </div>
                    )}
                  </div>
                  <div className="modern-stats-grid">
                    <div className="modern-stat-card">
                      <div className="modern-stat-icon"><FaCalendarAlt /></div>
                      <div className="modern-stat-value">{cleanText(providerInfo.totalAppointments ?? 0)}</div>
                      <div className="modern-stat-label">Total appointments</div>
                    </div>
                    <div className="modern-stat-card">
                      <div className="modern-stat-icon"><FaMoneyBillAlt /></div>
                      <div className="modern-stat-value">{cleanText(`${Number(providerInfo.revenue || 0).toLocaleString('en-US')} EGP`)}</div>
                      <div className="modern-stat-label">Revenue</div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}

/* ═════════════ REVIEWS (Enhanced) ═════════════ */
const REVIEW_STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
];

function Reviews() {
  const guard = useGuard401();
  const [state, setState] = useState('all');
  const [q, setQ] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const debouncedQ = useDebouncedValue(q, 350);
  const [ratingFilter, setRatingFilter] = useState(''); // '' | '1'..'5'
  const [rows, setRows] = useState([]);
  const [total, setTot] = useState(0);
  const [stats, setStats] = useState({ pending: 0, approved: 0 });
  const [ratingMix, setRatingMix] = useState({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [loading, setLoading] = useState(false);
  const [selectedReviews, setSelectedReviews] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false });
  const [expandedId, setExpandedId] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [noteSavingId, setNoteSavingId] = useState(null);

  const reviewsQuery = useCallback(
    (extra = {}) => {
      const params = new URLSearchParams();
      params.set('state', extra.state ?? state);
      params.set('page', String(extra.page ?? page));
      params.set('limit', String(extra.limit ?? pageSize));
      if (searchQ) params.set('q', searchQ);
      if (ratingFilter) params.set('rating', ratingFilter);
      return `/api/admin/reviews?${params.toString()}`;
    },
    [state, searchQ, ratingFilter, page, pageSize],
  );

  useEffect(() => {
    setSearchQ(debouncedQ.trim());
  }, [debouncedQ]);

  useEffect(() => {
    setPage(1);
  }, [state, searchQ, ratingFilter]);

  useEffect(() => {
    setExpandedId(null);
    setSelectedReviews([]);
  }, [state, searchQ, ratingFilter, page]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await httpAdmin.get(reviewsQuery());
      const items = pickArray(res);
      setRows(items);
      setTot(pickTotal(res, items));

      if (res?.ratingMix && typeof res.ratingMix === 'object') {
        setRatingMix({
          1: Number(res.ratingMix[1]) || 0,
          2: Number(res.ratingMix[2]) || 0,
          3: Number(res.ratingMix[3]) || 0,
          4: Number(res.ratingMix[4]) || 0,
          5: Number(res.ratingMix[5]) || 0,
        });
      } else {
        // Fallback: page-only mix if API not redeployed
        const buckets = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        items.forEach((r) => {
          const n = Math.min(5, Math.max(1, Number(r.rating) || 0));
          if (n) buckets[n] += 1;
        });
        setRatingMix(buckets);
      }

      setStats({
        pending: Number(res?.stats?.pending) || 0,
        approved: Number(res?.stats?.approved) || 0,
      });

      setNoteDrafts((prev) => {
        const next = { ...prev };
        items.forEach((r) => {
          if (next[r._id] == null) next[r._id] = r.adminNote || '';
        });
        return next;
      });
    } catch (e) {
      if (!guard(e)) console.error('Reviews load error', e);
      setRows([]);
      setTot(0);
    } finally {
      setLoading(false);
    }
  }, [reviewsQuery, guard]);

  useEffect(() => {
    load();
  }, [load]);

  const ratingMixTotal = useMemo(
    () => [5, 4, 3, 2, 1].reduce((sum, star) => sum + (ratingMix[star] || 0), 0),
    [ratingMix],
  );

  const hasActiveFilters = state !== 'all' || searchQ || ratingFilter;

  const approveReview = async (id) => {
    try {
      await httpAdmin.put(`/api/admin/reviews/${id}/approve`);
      await load();
    } catch (e) {
      if (!guard(e)) console.error('Approve review error:', e);
    }
  };

  const flagReview = async (id) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Unapprove review',
      message: 'Send this review back to Pending? It will be hidden from public listings until approved again.',
      confirmText: 'Unapprove',
      onConfirm: async () => {
        try {
          const adminNote = noteDrafts[id] != null ? noteDrafts[id] : undefined;
          await httpAdmin.put(`/api/admin/reviews/${id}/flag`, {
            ...(adminNote != null ? { adminNote } : {}),
          });
          await load();
          setConfirmDialog({ isOpen: false });
        } catch (e) {
          if (!guard(e)) console.error('Flag review error:', e);
        }
      },
    });
  };

  const saveNote = async (id) => {
    setNoteSavingId(id);
    try {
      await httpAdmin.put(`/api/admin/reviews/${id}/note`, {
        adminNote: noteDrafts[id] || '',
      });
      await load();
    } catch (e) {
      if (!guard(e)) console.error('Save review note error:', e);
    } finally {
      setNoteSavingId(null);
    }
  };

  const deleteReview = async (id) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Review',
      message: 'Are you sure you want to delete this review?',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await httpAdmin.del(`/api/admin/reviews/${id}`);
          await load();
          setConfirmDialog({ isOpen: false });
        } catch (e) {
          if (!guard(e)) console.error('Delete review error:', e);
        }
      },
    });
  };

  const bulkAction = async (action) => {
    if (selectedReviews.length === 0 || bulkBusy) return;

    const labels = {
      approve: {
        title: 'Approve reviews',
        message: `Approve ${selectedReviews.length} selected review(s)?`,
        confirm: 'Approve',
      },
      delete: {
        title: 'Delete reviews',
        message: `Permanently delete ${selectedReviews.length} selected review(s)?`,
        confirm: 'Delete',
      },
      flag: {
        title: 'Unapprove reviews',
        message: `Send ${selectedReviews.length} selected review(s) back to Pending?`,
        confirm: 'Unapprove',
      },
    };
    const meta = labels[action];
    if (!meta) return;

    setConfirmDialog({
      isOpen: true,
      title: meta.title,
      message: meta.message,
      confirmText: meta.confirm,
      onConfirm: async () => {
        const ids = [...selectedReviews];
        setConfirmDialog({ isOpen: false });
        setBulkBusy(true);
        setBulkProgress({ done: 0, total: ids.length });
        try {
          let done = 0;
          for (const reviewId of ids) {
            if (action === 'approve') {
              await httpAdmin.put(`/api/admin/reviews/${reviewId}/approve`);
            } else if (action === 'flag') {
              await httpAdmin.put(`/api/admin/reviews/${reviewId}/flag`, {});
            } else if (action === 'delete') {
              await httpAdmin.del(`/api/admin/reviews/${reviewId}`);
            }
            done += 1;
            setBulkProgress({ done, total: ids.length });
          }
          setSelectedReviews([]);
          await load();
        } catch (e) {
          if (!guard(e)) console.error('Bulk review action error:', e);
          await load();
        } finally {
          setBulkBusy(false);
          setBulkProgress({ done: 0, total: 0 });
        }
      },
    });
  };

  const runSearch = () => {
    setPage(1);
    setSearchQ(q.trim());
  };

  const toggleStarFilter = (star) => {
    const value = String(star);
    setRatingFilter((prev) => (prev === value ? '' : value));
  };

  return (
    <section className="adm-page adm-page--reviews">
      <header className="adm-page-head">
        <h2>Reviews ({total})</h2>
      </header>

      <div className="adm-users-tools adm-reviews-tools">
        <div className="adm-users-tools__row adm-users-tools__row--search">
          <form
            className="adm-search-group"
            onSubmit={(e) => {
              e.preventDefault();
              runSearch();
            }}
          >
            <input
              type="text"
              placeholder="Search user, provider, or comment…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="adm-search"
              aria-label="Search reviews"
            />
            <button type="submit" className="adm-search-btn" aria-label="Search">
              <FaSearch aria-hidden="true" />
              <span>Search</span>
            </button>
          </form>
        </div>

        <div className="adm-users-tools__row adm-users-tools__row--filters">
          <div className="adm-filter-chips" role="group" aria-label="Filter by status">
            {REVIEW_STATUS_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`adm-filter-chip${state === f.id ? ' is-active' : ''}`}
                onClick={() => setState(f.id)}
                aria-pressed={state === f.id}
              >
                {f.label}
                {f.id === 'pending' && stats.pending > 0 ? ` (${stats.pending})` : ''}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="adm-rating-breakdown" aria-label="Global rating mix">
        <div className="adm-rating-breakdown-head">
          <span className="adm-rating-breakdown-title">Rating mix</span>
          <div className="adm-rating-breakdown-meta">
            <span
              className={`adm-rating-breakdown-chip${ratingFilter ? ' is-active' : ''}`}
            >
              {ratingFilter ? (
                <>
                  <span className="adm-rating-breakdown-chip__star" aria-hidden="true">★</span>
                  {ratingFilter} star filter
                </>
              ) : (
                'All ratings'
              )}
            </span>
            <span className="adm-rating-breakdown-hint">Click a row to filter</span>
          </div>
        </div>
        <div className="adm-rating-breakdown-rows">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = ratingMix[star] || 0;
            const pct = ratingMixTotal ? Math.round((count / ratingMixTotal) * 100) : 0;
            const active = ratingFilter === String(star);
            return (
              <button
                key={star}
                type="button"
                className={`adm-rating-breakdown-row adm-rating-breakdown-row--btn${active ? ' is-active' : ''}`}
                onClick={() => toggleStarFilter(star)}
                aria-pressed={active}
                title={active ? `Clear ${star}★ filter` : `Filter ${star}★ reviews`}
              >
                <span className="adm-rating-breakdown-label">
                  <span className="adm-rating-breakdown-star">{star}</span>
                  <span className="adm-rating-breakdown-star-icon" aria-hidden="true">★</span>
                </span>
                <div className="adm-rating-breakdown-track" aria-hidden="true">
                  <div
                    className="adm-rating-breakdown-fill"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="adm-rating-breakdown-count">{count}</span>
              </button>
            );
          })}
        </div>
        {ratingFilter && (
          <button
            type="button"
            className="adm-rating-clear-filter"
            onClick={() => setRatingFilter('')}
          >
            Clear star filter
          </button>
        )}
      </div>

      {selectedReviews.length > 0 && (
        <div className={`adm-bulk-actions${bulkBusy ? ' is-busy' : ''}`}>
          <span>{selectedReviews.length} selected</span>
          {bulkBusy && (
            <span className="adm-bulk-progress" aria-live="polite">
              Working… {bulkProgress.done} / {bulkProgress.total}
            </span>
          )}
          <button type="button" onClick={() => bulkAction('approve')} className="btn-success" disabled={bulkBusy}>
            Approve
          </button>
          <button type="button" onClick={() => bulkAction('flag')} className="btn-warning" disabled={bulkBusy}>
            Unapprove
          </button>
          <button type="button" onClick={() => bulkAction('delete')} className="btn-danger" disabled={bulkBusy}>
            Delete
          </button>
          <button type="button" onClick={() => setSelectedReviews([])} className="btn-secondary" disabled={bulkBusy}>
            Clear
          </button>
        </div>
      )}

      <div className="adm-entity-list adm-entity-list--reviews">
        {loading ? (
          <div className="adm-loading">Loading reviews...</div>
        ) : rows.length === 0 ? (
          <div className="adm-entity-empty adm-entity-empty--users adm-entity-empty--reviews">
            <strong className="adm-entity-empty__title">No reviews found</strong>
            <p className="adm-entity-empty__desc">
              {hasActiveFilters
                ? 'Nothing matches this search or star filter. Try clearing filters.'
                : 'There are no reviews to moderate yet.'}
            </p>
            {hasActiveFilters && (
              <button
                type="button"
                className="btn-sm btn-secondary"
                onClick={() => {
                  setQ('');
                  setSearchQ('');
                  setState('all');
                  setRatingFilter('');
                }}
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="adm-entity-list-bar">
              <label className="adm-entity-check">
                <input
                  type="checkbox"
                  checked={selectedReviews.length === rows.length && rows.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedReviews(rows.map((r) => r._id));
                    } else {
                      setSelectedReviews([]);
                    }
                  }}
                />
                <span>Select all on page</span>
              </label>
            </div>
            {rows.map((review) => {
              const open = expandedId === review._id;
              const userName = review.user?.name || review.userName || 'Unknown';
              const initial = String(userName).trim().charAt(0).toUpperCase();
              const providerLabel = typeof review.provider === 'string'
                ? review.provider
                : (review.provider?.name || 'Unknown provider');
              const listingUrl = providerLabel && providerLabel !== 'Unknown provider'
                ? `/booking/${encodeURIComponent(providerLabel)}`
                : null;
              return (
                <article key={review._id} className={`adm-entity-card${open ? ' is-open' : ''}${review.flagged ? ' is-pending' : ''}`}>
                  <div
                    className="adm-entity-row"
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpandedId(open ? null : review._id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setExpandedId(open ? null : review._id);
                      }
                    }}
                    aria-expanded={open}
                  >
                    <label className="adm-entity-check" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedReviews.includes(review._id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedReviews((prev) => [...prev, review._id]);
                          } else {
                            setSelectedReviews((prev) => prev.filter((id) => id !== review._id));
                          }
                        }}
                      />
                    </label>
                    <div className="adm-entity-avatar" aria-hidden="true">{initial}</div>
                    <div className="adm-entity-main">
                      <div className="adm-entity-name">
                        {userName}
                        {review.flagged && <span className="adm-pending-pill">Needs review</span>}
                      </div>
                      <div className="adm-entity-sub">{providerLabel}</div>
                    </div>
                    <div className="adm-entity-meta">
                      <span className="adm-entity-rating" aria-label={`${review.rating} stars`}>
                        {'★'.repeat(Math.min(5, Math.max(0, Number(review.rating) || 0)))}
                        <span className="adm-entity-rating-empty">
                          {'★'.repeat(Math.max(0, 5 - Math.min(5, Math.max(0, Number(review.rating) || 0))))}
                        </span>
                      </span>
                      <span className={`badge ${review.flagged ? 'pending' : 'approved'}`}>
                        {review.flagged ? 'Pending' : 'Approved'}
                      </span>
                    </div>
                    <span className="adm-entity-chevron" aria-hidden="true">{open ? '▴' : '▾'}</span>
                  </div>

                  {open && (
                    <div className="adm-entity-panel">
                      <div className="adm-entity-stats">
                        <div className="adm-entity-stat">
                          <span className="adm-entity-stat-label">Rating</span>
                          <strong>{review.rating != null ? `${review.rating} / 5` : '—'}</strong>
                        </div>
                        <div className="adm-entity-stat">
                          <span className="adm-entity-stat-label">Provider</span>
                          <strong>{providerLabel}</strong>
                        </div>
                        <div className="adm-entity-stat">
                          <span className="adm-entity-stat-label">Status</span>
                          <strong>{review.flagged ? 'Pending' : 'Approved'}</strong>
                        </div>
                        <div className="adm-entity-stat">
                          <span className="adm-entity-stat-label">Date</span>
                          <strong>{review.createdAt ? new Date(review.createdAt).toLocaleDateString() : '—'}</strong>
                        </div>
                      </div>
                      <div className="adm-entity-comment">
                        {review.comment ? review.comment : <span className="adm-entity-comment-empty">No comment</span>}
                      </div>

                      <div className="adm-review-note">
                        <label className="adm-review-note__label" htmlFor={`review-note-${review._id}`}>
                          Admin note
                        </label>
                        <textarea
                          id={`review-note-${review._id}`}
                          className="adm-review-note__input"
                          rows={2}
                          maxLength={500}
                          value={noteDrafts[review._id] ?? review.adminNote ?? ''}
                          onChange={(e) =>
                            setNoteDrafts((prev) => ({ ...prev, [review._id]: e.target.value }))
                          }
                          placeholder="Internal note (not shown publicly)"
                        />
                        <button
                          type="button"
                          className="btn-sm btn-secondary"
                          disabled={noteSavingId === review._id}
                          onClick={() => saveNote(review._id)}
                        >
                          {noteSavingId === review._id ? 'Saving…' : 'Save note'}
                        </button>
                      </div>

                      <div className="adm-entity-actions">
                        {listingUrl && (
                          <a
                            href={listingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-sm btn-secondary adm-provider-listing-link"
                            title="Open public listing"
                          >
                            <FaExternalLinkAlt aria-hidden="true" />
                            <span>View provider</span>
                          </a>
                        )}
                        {review.flagged ? (
                          <button
                            type="button"
                            onClick={() => approveReview(review._id)}
                            className="btn-sm btn-success"
                          >
                            Approve
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => flagReview(review._id)}
                            className="btn-sm btn-warning"
                          >
                            Unapprove
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => deleteReview(review._id)}
                          className="btn-sm btn-danger"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </>
        )}
      </div>
      <PaginationBar page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
      />
    </section>
  );
}

/* ═════════════ DELETE ACCOUNT REQUESTS ═════════════ */
function deleteUrgency(scheduledDeletionAt, status) {
  if (status !== 'pending' || !scheduledDeletionAt) return null;
  const due = new Date(scheduledDeletionAt).getTime();
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;
  if (due < now) return 'overdue';
  if (due <= now + day) return 'due-today';
  if (due <= now + 7 * day) return 'due-soon';
  return null;
}

function DeleteAccountRequests() {
  const guard = useGuard401();
  const [list, setList] = useState([]);
  const [total, setTot] = useState(0);
  const [stats, setStats] = useState({ pending: 0, dueToday: 0, processedThisWeek: 0 });
  const [status, setStatus] = useState('pending');
  const [q, setQ] = useState('');
  const debouncedQ = useDebouncedValue(q, 300);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false });
  const [rowBusy, setRowBusy] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    setPage(1);
  }, [status, debouncedQ]);

  useEffect(() => {
    setExpandedId(null);
    setSelected([]);
  }, [status, page, debouncedQ]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
      });
      if (status) params.set('status', status);
      if (debouncedQ.trim()) params.set('q', debouncedQ.trim());
      const res = await httpAdmin.get(`/api/admin/delete-account-requests?${params}`);
      const items = pickArray(res);
      const tot = pickTotal(res, items);
      setList(items);
      setTot(tot);
      setStats({
        pending: res?.stats?.pending ?? 0,
        dueToday: res?.stats?.dueToday ?? 0,
        processedThisWeek: res?.stats?.processedThisWeek ?? 0,
      });
    } catch (e) {
      if (!guard(e)) console.error('DeleteAccountRequests load error', e);
      setList([]);
      setTot(0);
    } finally {
      setLoading(false);
    }
  }, [status, page, debouncedQ, guard]);

  useEffect(() => {
    load();
  }, [load]);

  const processDueGlobal = async () => {
    setProcessing(true);
    try {
      await httpAdmin.post('/api/admin/delete-account-requests/process');
      await load();
    } catch (e) {
      if (!guard(e)) console.error('Process error', e);
    } finally {
      setProcessing(false);
    }
  };

  const cancelRequest = (r) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Cancel delete request',
      message: `Cancel the pending delete request for ${r.email}? The account will not be deleted on the scheduled date.`,
      confirmText: 'Cancel request',
      onConfirm: async () => {
        setRowBusy(r._id);
        try {
          await httpAdmin.post(`/api/admin/delete-account-requests/${r._id}/cancel`);
          await load();
          setConfirmDialog({ isOpen: false });
        } catch (e) {
          if (!guard(e)) console.error('Cancel delete request error', e);
        } finally {
          setRowBusy(null);
        }
      },
    });
  };

  const extendRequest = (r) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Extend grace period',
      message: `Extend the grace period for ${r.email} by 30 days? Scheduled deletion moves forward 30 days.`,
      confirmText: 'Extend 30 days',
      onConfirm: async () => {
        setRowBusy(r._id);
        try {
          await httpAdmin.post(`/api/admin/delete-account-requests/${r._id}/extend`, { days: 30 });
          await load();
          setConfirmDialog({ isOpen: false });
        } catch (e) {
          if (!guard(e)) console.error('Extend delete request error', e);
        } finally {
          setRowBusy(null);
        }
      },
    });
  };

  const processOne = (r) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Process delete now',
      message: `Soft-delete ${r.email} now? This cannot be undone from this screen.`,
      confirmText: 'Process now',
      onConfirm: async () => {
        setRowBusy(r._id);
        try {
          await httpAdmin.post(`/api/admin/delete-account-requests/${r._id}/process`);
          await load();
          setConfirmDialog({ isOpen: false });
        } catch (e) {
          if (!guard(e)) console.error('Process one delete request error', e);
        } finally {
          setRowBusy(null);
        }
      },
    });
  };

  const bulkAction = (action) => {
    const pendingIds = selected.filter((id) => list.find((r) => r._id === id && r.status === 'pending'));
    if (!pendingIds.length || bulkBusy) return;
    const isExtend = action === 'extend';
    setConfirmDialog({
      isOpen: true,
      title: isExtend ? 'Bulk extend' : 'Bulk cancel',
      message: isExtend
        ? `Extend grace period by 30 days for ${pendingIds.length} pending request(s)?`
        : `Cancel ${pendingIds.length} pending request(s)? Accounts will not be deleted on the scheduled date.`,
      confirmText: isExtend ? 'Extend selected' : 'Cancel selected',
      onConfirm: async () => {
        setBulkBusy(true);
        try {
          for (const id of pendingIds) {
            if (isExtend) {
              await httpAdmin.post(`/api/admin/delete-account-requests/${id}/extend`, { days: 30 });
            } else {
              await httpAdmin.post(`/api/admin/delete-account-requests/${id}/cancel`);
            }
          }
          setSelected([]);
          await load();
          setConfirmDialog({ isOpen: false });
        } catch (e) {
          if (!guard(e)) console.error('Bulk delete-request action error', e);
        } finally {
          setBulkBusy(false);
        }
      },
    });
  };

  const selectAll = (checked) => {
    setSelected(checked ? list.map((r) => r._id) : []);
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString();
  };

  const statusBadgeClass = (s) => {
    if (s === 'pending') return 'pending';
    if (s === 'processed') return 'approved';
    if (s === 'cancelled') return 'inactive';
    return 'inactive';
  };

  const urgencyLabel = (u) => {
    if (u === 'overdue') return 'Overdue';
    if (u === 'due-today') return 'Due today';
    if (u === 'due-soon') return 'Due soon';
    return '';
  };

  const hasFilters = Boolean(debouncedQ.trim() || status !== 'pending');

  return (
    <section className="adm-page delete-requests-page">
      <header className="adm-page-head delete-requests-head">
        <div className="delete-requests-title-block">
          <h2>Delete Account Requests{total != null ? ` (${total})` : ''}</h2>
          <p className="delete-requests-desc">
            Requests are automatically processed 90 days after submission. Accounts are soft-deleted when due.
          </p>
        </div>
        <div className="adm-filters delete-requests-actions">
          <input
            type="search"
            className="adm-search"
            placeholder="Search name or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search delete requests"
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="adm-select"
            aria-label="Filter by status"
          >
            <option value="pending">Pending</option>
            <option value="processed">Processed</option>
            <option value="cancelled">Cancelled</option>
            <option value="">All</option>
          </select>
          <button
            type="button"
            className="delete-requests-process-btn"
            onClick={processDueGlobal}
            disabled={processing}
          >
            {processing ? 'Processing…' : 'Process due now'}
          </button>
        </div>
      </header>

      <div className="adm-del-stats" aria-label="Delete request stats">
        <div className="adm-del-stat">
          <span className="adm-del-stat__label">Pending</span>
          <strong className="adm-del-stat__value">{stats.pending}</strong>
        </div>
        <div className="adm-del-stat adm-del-stat--warn">
          <span className="adm-del-stat__label">Due today</span>
          <strong className="adm-del-stat__value">{stats.dueToday}</strong>
        </div>
        <div className="adm-del-stat">
          <span className="adm-del-stat__label">Processed (7d)</span>
          <strong className="adm-del-stat__value">{stats.processedThisWeek}</strong>
        </div>
      </div>

      {selected.length > 0 && (
        <div className={`adm-bulk-actions${bulkBusy ? ' is-busy' : ''}`}>
          <span>{selected.length} selected</span>
          <button type="button" className="btn-info" disabled={bulkBusy} onClick={() => bulkAction('extend')}>
            Extend 30d
          </button>
          <button type="button" className="btn-danger" disabled={bulkBusy} onClick={() => bulkAction('cancel')}>
            Cancel
          </button>
          <button type="button" className="btn-secondary" disabled={bulkBusy} onClick={() => setSelected([])}>
            Clear
          </button>
        </div>
      )}

      <div className="adm-entity-list adm-entity-list--delete-requests">
        {loading ? (
          <div className="adm-loading">Loading…</div>
        ) : list.length === 0 ? (
          <div className="adm-entity-empty adm-entity-empty--delete">
            <strong className="adm-entity-empty__title">
              {hasFilters ? 'No matching delete requests' : 'Delete queue is clear'}
            </strong>
            <p className="adm-entity-empty__desc">
              {hasFilters
                ? 'Try another search or switch status filters.'
                : 'No pending account deletions right now. New requests appear here after clients submit them.'}
            </p>
            {hasFilters && (
              <button
                type="button"
                className="btn-sm btn-secondary"
                onClick={() => {
                  setQ('');
                  setStatus('pending');
                }}
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="adm-entity-list-bar">
              <label className="adm-entity-check">
                <input
                  type="checkbox"
                  checked={selected.length === list.length && list.length > 0}
                  onChange={(e) => selectAll(e.target.checked)}
                />
                <span>Select all on page</span>
              </label>
            </div>
            {list.map((r) => {
              const open = expandedId === r._id;
              const label = r.name || r.email || 'Request';
              const initial = String(label).trim().charAt(0).toUpperCase();
              const urgency = deleteUrgency(r.scheduledDeletionAt, r.status);
              return (
                <article key={r._id} className={`adm-entity-card${open ? ' is-open' : ''}`}>
                  <div
                    className="adm-entity-row"
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpandedId(open ? null : r._id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setExpandedId(open ? null : r._id);
                      }
                    }}
                    aria-expanded={open}
                  >
                    <label className="adm-entity-check" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.includes(r._id)}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setSelected((prev) => (on ? [...prev, r._id] : prev.filter((id) => id !== r._id)));
                        }}
                      />
                    </label>
                    <div className="adm-entity-avatar" aria-hidden="true">{initial}</div>
                    <div className="adm-entity-main">
                      <div className="adm-entity-name">{r.email || 'No email'}</div>
                      <div className="adm-entity-sub">
                        {r.name || 'Unnamed'}
                        {r.scheduledDeletionAt
                          ? ` · Due ${new Date(r.scheduledDeletionAt).toLocaleDateString()}`
                          : ''}
                      </div>
                    </div>
                    <div className="adm-entity-meta">
                      {urgency && (
                        <span className={`adm-urgency-chip adm-urgency-chip--${urgency}`}>
                          {urgencyLabel(urgency)}
                        </span>
                      )}
                      <span className={`badge ${statusBadgeClass(r.status)}`}>
                        {r.status || '—'}
                      </span>
                    </div>
                    <span className="adm-entity-chevron" aria-hidden="true">{open ? '▴' : '▾'}</span>
                  </div>

                  {open && (
                    <div className="adm-entity-panel">
                      <div className="adm-entity-stats">
                        <div className="adm-entity-stat">
                          <span className="adm-entity-stat-label">Name</span>
                          <strong>{r.name || '—'}</strong>
                        </div>
                        <div className="adm-entity-stat">
                          <span className="adm-entity-stat-label">Submitted</span>
                          <strong>{formatDate(r.submittedAt)}</strong>
                        </div>
                        <div className="adm-entity-stat">
                          <span className="adm-entity-stat-label">Scheduled</span>
                          <strong>{formatDate(r.scheduledDeletionAt)}</strong>
                        </div>
                        <div className="adm-entity-stat">
                          <span className="adm-entity-stat-label">Status</span>
                          <strong style={{ textTransform: 'capitalize' }}>{r.status || '—'}</strong>
                        </div>
                      </div>
                      {r.reason ? (
                        <div className="adm-entity-comment">
                          <strong>Reason: </strong>{r.reason}
                        </div>
                      ) : (
                        <div className="adm-entity-comment">
                          <span className="adm-entity-comment-empty">No reason provided</span>
                        </div>
                      )}
                      <div className="adm-entity-actions">
                        {r.status === 'pending' ? (
                          <>
                            <button
                              type="button"
                              className="btn-sm btn-info"
                              disabled={rowBusy === r._id}
                              onClick={() => extendRequest(r)}
                            >
                              Extend 30d
                            </button>
                            <button
                              type="button"
                              className="btn-sm btn-warning"
                              disabled={rowBusy === r._id}
                              onClick={() => processOne(r)}
                            >
                              Process now
                            </button>
                            <button
                              type="button"
                              className="btn-sm btn-danger"
                              disabled={rowBusy === r._id}
                              onClick={() => cancelRequest(r)}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <span className="delete-requests-no-actions">No actions for this status</span>
                        )}
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </>
        )}
      </div>

      <PaginationBar page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
      />
    </section>
  );
}

/* ═════════════ SHELL (sidebar + nested routes) ═════════════ */
function Shell() {
  const nav = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminUser] = useState(() => readStoredAdminUser());
  const [liveActiveUsers, setLiveActiveUsers] = useState(null);
  const [pendingProviders, setPendingProviders] = useState(0);

  const routeKey = location.pathname.replace(/^\/admin\/?/, '').split('/')[0] || 'users';
  const pageTitle = ADMIN_PAGE_TITLES[routeKey] || 'Admin';
  const displayName = adminUser?.name || adminUser?.email || 'Admin';
  const avatarInitial = String(displayName).trim().charAt(0).toUpperCase() || 'A';

  const handlePendingProviders = useCallback((count) => {
    setPendingProviders(Number(count) || 0);
  }, []);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarOpen && window.innerWidth <= 768) {
        const sidebar = document.querySelector('.adm-sidebar');
        const menuBtn = document.querySelector('.mobile-menu-btn');
        
        if (sidebar && !sidebar.contains(event.target) && menuBtn && !menuBtn.contains(event.target)) {
          setSidebarOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarOpen]);

  // Header live badge — poll realtime active users on every admin page
  useEffect(() => {
    let cancelled = false;
    const loadLive = async () => {
      try {
        const data = await httpAdmin.get('/api/admin/analytics/realtime');
        if (cancelled) return;
        const next = Number(data?.activeUsers ?? data?.live?.activeUsers);
        setLiveActiveUsers(Number.isFinite(next) ? next : 0);
      } catch {
        /* keep last known value on transient errors */
      }
    };
    loadLive();
    const interval = setInterval(loadLive, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // Sidebar pending-providers badge (poll when not on Providers tab)
  useEffect(() => {
    if (routeKey === 'providers') return undefined;
    let cancelled = false;
    const loadPending = async () => {
      try {
        const data = await httpAdmin.get('/api/admin/providers?state=pending&page=1&limit=1');
        if (!cancelled) setPendingProviders(Number(data?.stats?.pending ?? data?.total) || 0);
      } catch {
        /* keep last known */
      }
    };
    loadPending();
    const interval = setInterval(loadPending, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [routeKey]);

  const logout = useCallback(async () => {
    await httpAdmin.logout();
    nav('/admin/login?reason=logout', { replace: true });
  }, [nav]);

  return (
    <div className="adm-dashboard">
      {/* Mobile Menu Button */}
      <button
        className="mobile-menu-btn"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Toggle mobile menu"
        aria-expanded={sidebarOpen}
      >
        <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
        </svg>
      </button>

      <aside className={`adm-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="adm-sidebar-header">
          <div className="adm-sidebar-brand">
            <div className="adm-sidebar-brand-text">
              <img
                src={ADMIN_HERO_LOGO}
                alt="Ehgezly"
                className="adm-sidebar-wordmark"
                draggable="false"
              />
              <span className="adm-sidebar-subtitle">Admin</span>
            </div>
          </div>
        </div>
        <nav className="adm-nav">
          <div className="adm-nav-section">
            <div className="adm-nav-section-title">Management</div>
            <SectionButton to="users" icon={FaUsers} label="Users" />
            <SectionButton to="providers" icon={FaStoreAlt} label="Providers" badge={pendingProviders} />
            <SectionButton to="reviews" icon={FaStarHalfAlt} label="Reviews" />
            <SectionButton to="businesses" icon={FaPlus} label="List Business" />
            <SectionButton to="notifications" icon={FaBell} label="Notifications" />
            <SectionButton to="shop-owner-outreach" icon={FaEnvelope} label="Shop Owner Email" />
            <SectionButton to="delete-account-requests" icon={FaTrashAlt} label="Delete requests" />
          </div>
          <div className="adm-nav-section">
            <div className="adm-nav-section-title">Analytics</div>
            <SectionButton to="analytics" icon={FaChartBar} label="Analytics" />
            <SectionButton to="activity" icon={FaClipboardList} label="Activity" />
          </div>
          <div className="adm-nav-section">
            <div className="adm-nav-section-title">System</div>
            <SectionButton to="health" icon={FaHeartbeat} label="Health" />
          </div>
          <div className="adm-logout-wrapper">
            <button onClick={logout} className="adm-logout-btn">
              <FaSignOutAlt />
              Logout
            </button>
          </div>
        </nav>
      </aside>

      <header className="adm-header">
        <div className="adm-header-left">
          <h1>{pageTitle}</h1>
        </div>
        <div className="adm-header-right">
          <div
            className="adm-live-badge"
            title="People active in the last 15 minutes (logins, bookings, analytics, sockets, and admins using this dashboard)"
          >
            <span className="live-dot" aria-hidden="true" />
            <span className="adm-live-badge-label">Live</span>
            <strong aria-label={`${liveActiveUsers == null ? 'loading' : liveActiveUsers} active`}>
              {liveActiveUsers == null ? '—' : liveActiveUsers}
            </strong>
          </div>
          <div className="adm-user-info" title={adminUser?.email || displayName}>
            <div className="adm-user-avatar" aria-hidden="true">{avatarInitial}</div>
            <span className="adm-user-name">{displayName}</span>
          </div>
        </div>
      </header>

      <main className="adm-main">
        <Routes>
          <Route path="users" element={<Users />} />
          <Route path="providers" element={<Providers onPendingCountChange={handlePendingProviders} />} />
          <Route path="reviews" element={<Reviews />} />
          <Route path="businesses" element={<AdminBusinessListing />} />
          <Route path="notifications" element={<NotificationManagement />} />
          <Route path="shop-owner-outreach" element={<ShopOwnerOutreach />} />
          <Route path="delete-account-requests" element={<DeleteAccountRequests />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="activity" element={<AdminActivity />} />
          <Route path="health" element={<AdminHealth />} />
          <Route path="*" element={<Navigate to="users" replace />} />
        </Routes>
      </main>
    </div>
  );
}

/* ═════════════ Root export with redirect-guard ═════════════ */
export default function AdminDashboard() {
  const token = !!localStorage.getItem('adm-token');
  const nav = useNavigate();
  const loc = useLocation();

  const isAuthPage =
    loc.pathname === '/admin/login' || loc.pathname === '/admin/forgot-password';

  /* keeps URL in sync with auth state */
  useEffect(() => {
    if (token && isAuthPage) {
      nav('/admin/users', { replace: true });
    }
    if (!token && loc.pathname.startsWith('/admin/') && !isAuthPage) {
      nav('/admin/login?reason=session', { replace: true });
    }
  }, [token, loc.pathname, nav, isAuthPage]);

  return (
    <Routes>
      {!token && <Route path="login" element={<AdminLoginPage />} />}
      {!token && <Route path="forgot-password" element={<AdminForgotPasswordPage />} />}
      {token && <Route path="/*" element={<Shell />} />}
    </Routes>
  );
}
